import { type FocusEvent, type FormEvent, useState } from 'react';
import { Button, Dialog, Heading, Inline, Input, Stack, vars } from '@etg/wings';
import { ExpandableContent } from '@eti/components';
import { css } from '@eti/styles';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { constants } from '@eti/utils';
import { endpoints, routes } from '../../../../constants/routesAndApisConstants';
import AddonOrdersRadioGroup from '../../../order-details/components/overview/payment/AddonOrdersRadioGroup';
import type { AddOnOrdersType } from '../../../order-details/models';

const topBlockClass = css`
  border-bottom: 1px solid ${vars.colors.divider};
  margin-inline: -24px;
  padding-bottom: 24px;
  width: calc(100% + 48px);
`;

const bottomBlockClass = css`
  background-color: ${vars.colors.page.background.primary};
  border-bottom-left-radius: ${vars.card.borderRadius};
  border-bottom-right-radius: ${vars.card.borderRadius};
  margin-bottom: -24px;
  margin-inline: -24px;
  padding: 24px;
  width: calc(100% + 48px);
`;

interface ReceiptPrintDialogProps {
  addOnOrders: AddOnOrdersType;
  isOpen: boolean;
  onClose: () => void;
  orderNumber: string;
  orderReferenceSerialized?: string | null;
  purchaseDate: string;
  shouldShowPrintConfirmationButton: boolean;
}

type CompanyType = {
  company?: {
    city?: string;
    coAddress?: string;
    companyAddress?: string;
    companyName?: string;
    streetAddress?: string;
    vatNumber?: string;
    zipCode?: string;
  };
};

const createGetParam = (name: string, value: string) =>
  value ? `&${name}=${encodeURIComponent(value)}` : '';

const getOrderChangeNumber = (selectedOrderToPrint: string | null, orderNumber: string) => {
  if (selectedOrderToPrint === orderNumber || selectedOrderToPrint === null) {
    return '';
  }
  return `&orderChangeNumber=${selectedOrderToPrint.split('-')[1]}`;
};

const ReceiptPrintDialog = ({
  addOnOrders = [],
  isOpen,
  onClose,
  orderNumber,
  orderReferenceSerialized,
  purchaseDate,
  shouldShowPrintConfirmationButton = true,
}: ReceiptPrintDialogProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { language } = useSiteContext();
  const [selectedOrderToPrint, setSelectedOrderToPrint] = useState(
    addOnOrders && addOnOrders.length > 0 ? null : orderNumber,
  );
  const [hasChosenOrderToPrint, setHasChosenOrderToPrint] = useState(false);

  const inputFieldNames = [
    {
      label: t('Receipt.ReceiptDetails.PrintModal.StreetAddressLabel'),
      name: 'company.streetAddress',
      maxChars: 128,
    },
    {
      label: t('Receipt.ReceiptDetails.PrintModal.CompanyNameLabel'),
      name: 'company.companyName',
      maxChars: 128,
    },
    {
      label: t('Receipt.ReceiptDetails.PrintModal.CompanyStreetAddressLabel'),
      name: 'company.companyAddress',
      maxChars: 128,
    },
    {
      label: t('Receipt.ReceiptDetails.PrintModal.CompanyCityLabel'),
      name: 'company.city',
      maxChars: 80,
    },
    {
      label: t('Receipt.ReceiptDetails.PrintModal.CompanyPostCodeLabel'),
      name: 'company.zipCode',
      maxChars: 30,
    },
    {
      label: t('Receipt.ReceiptDetails.PrintModal.CompanyVatNumberLabel'),
      name: 'company.vatNumber',
      maxChars: 25,
    },
    {
      label: t('Receipt.ReceiptDetails.PrintModal.CompanyCareOfNameLabel'),
      name: 'company.coAddress',
      maxChars: 128,
    },
  ];

  const handleSelectOrderToPrint = (event: FocusEvent<HTMLInputElement>) => {
    setSelectedOrderToPrint(event.target.value);
    setHasChosenOrderToPrint(true);
  };

  const showInputFieldsAndButton =
    (addOnOrders && addOnOrders.length === 0) || hasChosenOrderToPrint;
  const dialogTitle = shouldShowPrintConfirmationButton
    ? t('Receipt.ReceiptDetails.PrintModal.Heading')
    : t('Receipt.ReceiptDetails.PrintModal.CreateBusinessHeading');

  const handleBusinessSubmit = ({ company = {} }: CompanyType) => {
    const getParams = Object.entries(company)
      .map(([key, value]) => createGetParam(key, value))
      .join('');

    window.open(
      `${window.location.origin}${
        routes.INVOICE_RECEIPT_PDF
      }?orderNumber=${orderNumber}${getOrderChangeNumber(
        selectedOrderToPrint,
        orderNumber,
      )}${getParams}`,
    );
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries());
    const city = data['company.city'] as string;
    const coAddress = data['company.coAddress'] as string;
    const companyAddress = data['company.companyAddress'] as string;
    const companyName = data['company.companyName'] as string;
    const streetAddress = data['company.streetAddress'] as string;
    const vatNumber = data['company.vatNumber'] as string;
    const zipCode = data['company.zipCode'] as string;

    handleBusinessSubmit({
      company: {
        city,
        coAddress,
        companyAddress,
        companyName,
        streetAddress,
        vatNumber,
        zipCode,
      },
    });
  };

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      data-testid="invoice-dialog"
      isOpen={isOpen}
      onDismiss={onClose}
      title={dialogTitle}
    >
      {shouldShowPrintConfirmationButton && (
        <form action={endpoints.CONFIRMATION_GENERATE} method="post" target="_blank">
          <input
            name="orderReferenceSerialized"
            type="hidden"
            value={orderReferenceSerialized ?? ''}
          />
          <input name="_LAYOUT_TYPE" type="hidden" value="print" />
          <input name="orderId" type="hidden" value={parseInt(orderNumber, 36)} />
          <input name="business" type="hidden" value="false" />
          <input name="ibe.ol-lang" type="hidden" value={language.id} />
          <Inline align="center" className={topBlockClass}>
            <Button
              data-testid="receiptPrintModal-printOrdinary-button"
              type="submit"
              variant="primary"
            >
              {t('Receipt.ReceiptDetails.PrintModal.PrintClassicReceiptButton')}
            </Button>
          </Inline>
        </form>
      )}
      {p('Invoice.Generation.Enabled') && (
        <Stack className={shouldShowPrintConfirmationButton ? bottomBlockClass : ''}>
          {shouldShowPrintConfirmationButton && (
            <Inline align="center">
              <Heading level={3}>
                {t('Receipt.ReceiptDetails.PrintModal.CreateBusinessHeading')}
              </Heading>
            </Inline>
          )}
          {addOnOrders && addOnOrders.length > 0 && (
            <AddonOrdersRadioGroup
              addOnOrders={addOnOrders}
              dataTestIdPrefix="invoice"
              onChange={handleSelectOrderToPrint}
              orderNumber={orderNumber}
              purchaseDate={purchaseDate}
              selectedValue={selectedOrderToPrint}
            />
          )}
          <ExpandableContent isExpanded={showInputFieldsAndButton}>
            <form onSubmit={handleSubmit}>
              <Stack>
                {inputFieldNames.map((field) => (
                  <Stack key={field.name} spacing={4}>
                    <Input.Label htmlFor={field.name}>{field.label}</Input.Label>
                    <Input
                      className={constants.MOUSE_FLOW_CLASS_NAMES}
                      data-testid={`${field.name}-input`}
                      id={field.name}
                      maxLength={field.maxChars}
                      name={field.name}
                      type="text"
                    />
                  </Stack>
                ))}
                <Button
                  aria-label={`${t(
                    'Receipt.ReceiptDetails.PrintModal.PrintCompanyReceiptButton',
                  )}, ${t('ScreenReader.Link.OpensInNewTab.AriaLabel.Text')}`}
                  data-testid="receiptPrintModal-printCompany-button"
                  type="submit"
                  variant="primary"
                >
                  {t('Receipt.ReceiptDetails.PrintModal.PrintCompanyReceiptButton')}
                </Button>
              </Stack>
            </form>
          </ExpandableContent>
        </Stack>
      )}
    </Dialog>
  );
};

export default ReceiptPrintDialog;
